import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomChartReport } from 'src/app/interfaces/custom-chart-report/custom-chart-report.interface';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { WalletReportFilterModel } from 'src/app/models/wallet/wallet-report-filter.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryPeriodService } from 'src/app/services/inventory/inventory-period.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { WalletReportService } from 'src/app/services/wallet/wallet-report.service';

@Component({
  selector: 'app-total-income-per-month',
  templateUrl: './total-income-per-month.component.html',
  styleUrls: ['./total-income-per-month.component.scss'],
  standalone: false
})
export class TotalIncomePerMonthComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  branchId: number = 0;
  changeFilters: boolean = false;
  customChartReport: CustomChartReport;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formWalletReportFilter: FormGroup = new FormGroup({});
  listBranch: any[] = [];
  listEnterprise: any[] = [];
  nameWindow: string = 'Informe de Ingresos Totales por Mes';
  projectName = ProjectName.projectName;

  constructor(private branchService: BranchService,
    private enterpriseService: EnterpriseService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private inventoryPeriodService: InventoryPeriodService,
    private title: Title,
    private walletReportService: WalletReportService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getLastInventoryPeriod();
    await this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists();
    await this.getFirstEnterpriseByBranch(this.branchId);
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeBranch(branchId: number | null) {
    if (branchId != 0 && branchId != null) {
      this.branchId = branchId;
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    } else {
      this.branchId = 0;
      this.formWalletReportFilter.controls["branchId"].patchValue(0);
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    }
    this.changeFilter();
    this.getAllLists('enterprises');
  }

  async changeClient(clientId: number) {
    if (clientId === 0 || clientId === null) {
      this.formWalletReportFilter.controls["clientId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeEnterprise(enterpriseId: number | null) {
    if (enterpriseId === 0 || enterpriseId === null) {
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
    await this.getLastInventoryPeriod();
    await this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists('enterprises');
    this.getFirstEnterpriseByBranch(this.branchId);
  }

  async exportExcel() {
    if (this.changeFilters) {
      const dateInitial = this.formWalletReportFilter.get("initialDate")?.value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);
      const dateFinal = this.formWalletReportFilter.get("finalDate")?.value;
      const finalDate = `${dateFinal.getFullYear()}-${(dateFinal.getMonth() + 1).toString().padStart(2, '0')}`;
      const periodFinal = finalDate.slice(5);
      const yearFinal = finalDate.slice(0, -3);

      const modelWalletReportFilter = this.formWalletReportFilter.value as WalletReportFilterModel;
      modelWalletReportFilter.year = parseInt(year);
      modelWalletReportFilter.period = parseInt(period);
      modelWalletReportFilter.yearFinal = parseInt(yearFinal);
      modelWalletReportFilter.periodFinal = parseInt(periodFinal);
      try {
        const response = await firstValueFrom(this.walletReportService.exportWalletReportTotalIncomePerMonth(modelWalletReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Ingresos Totales por Mes.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'enterprises'>) {
    try {
      const lists = {
        branches: async () => {
          this.listBranch = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
        },
        enterprises: async () => {
          this.listEnterprise = await firstValueFrom(this.enterpriseService.getEnterprisesByBranchIdForSelect(this.branchId));
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.enterprises()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      this.branchId = (await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync())).branchId;
      this.formWalletReportFilter.controls["branchId"].patchValue(this.branchId);
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getFirstEnterpriseByBranch(branchId: number) {
    try {
      const enterprise = await firstValueFrom(this.enterpriseService.getFirstEnterpriseByBranch(branchId));
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(enterprise.enterpriseId);
    } catch (error) {
      console.error("Error al consultar la empresa:", error);
    }
  }

  async getLastInventoryPeriod() {
    try {
      const lastInventory = await firstValueFrom(this.inventoryPeriodService.getLastInventoryPeriod());
      this.formWalletReportFilter.controls["initialDate"].patchValue(moment(lastInventory.initialDate).startOf('month').toDate());
      this.formWalletReportFilter.controls["finalDate"].patchValue(moment(lastInventory.initialDate).startOf('month').toDate());
    } catch (error) {
      console.error("Error al consultar la empresa:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formWalletReportFilter.controls).forEach(key => {
      const control = this.formWalletReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formWalletReportFilter.valid) {
      const dateInitial = this.formWalletReportFilter.get("initialDate")?.value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);
      const dateFinal = this.formWalletReportFilter.get("finalDate")?.value;
      const finalDate = `${dateFinal.getFullYear()}-${(dateFinal.getMonth() + 1).toString().padStart(2, '0')}`;
      const periodFinal = finalDate.slice(5);
      const yearFinal = finalDate.slice(0, -3);

      if (finalDate < initialDate) {
        this.toastHandlerService.showToast('warning', 'La fecha final no puede ser menor a la fecha inicial.');
        return;
      }

      const modelWalletReportFilter = this.formWalletReportFilter.value as WalletReportFilterModel;
      modelWalletReportFilter.year = parseInt(year);
      modelWalletReportFilter.period = parseInt(period);
      modelWalletReportFilter.yearFinal = parseInt(yearFinal);
      modelWalletReportFilter.periodFinal = parseInt(periodFinal);
      try {
        this.dataReport = await firstValueFrom(this.walletReportService.walletReportTotalIncomePerMonth(modelWalletReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;
          //Capturo los datos para la grafica
          const result: any = [];
          let serie: any = [];
          let yearOld: number = 0;
          this.dataReport.data.forEach(row => {
            const totalValue = Number(row.totalValue);
            if (yearOld !== 0 && yearOld !== row.year) {
              result.push(
                {
                  name: yearOld,
                  series: serie
                }
              );
              serie = [];
              serie.push(
                {
                  name: row.month,
                  value: totalValue
                }
              );
            } else {
              serie.push(
                {
                  name: row.month,
                  value: totalValue
                }
              );
            }
            yearOld = row.year;
          });
          if (this.dataReport.data.length > 0) {
            result.push(
              {
                name: yearOld,
                series: serie
              }
            );
          }
          this.customChartReport = {
            result: result,
            legendTitle: '',
            showXAxisLabel: true,
            xAxisLabel: 'Mes',
            showYAxisLabel: true,
            yAxisLabel: 'Ingresos'
          };
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      initialDate: {
        required: 'El campo Desde es obligatorio.'
      },
      finalDate: {
        required: 'El campo Hasta es obligatorio.'
      },
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      enterpriseId: {
        required: 'El campo Empresa es obligatorio.',
        min: 'Debe seleccionar una Empresa válida.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formWalletReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formWalletReportFilter = this.formBuilder.group({
      initialDate: ['', [Validators.required]],
      finalDate: ['', [Validators.required]],
      branchId: [0, [Validators.required, Validators.min(1)]],
      enterpriseId: [0, [Validators.required, Validators.min(1)]]
    });
  }

}
